import React from 'react';
import { TextEditorOverlay } from '../editing/TextEditor';
import { FloatingTextFormatToolbar } from '../editing/TextFormatToolbar';
import { ShapeTextToolbar } from './ShapeTextToolbar';
import { ViewportTransform } from '../core/types';
import { EditableElement } from '../editing/types';
import { useTextEditor } from '../editing/useTextEditor';

type UseTextEditorReturn = ReturnType<typeof useTextEditor>;

interface CanvasOverlaysProps {
  textEditor: UseTextEditorReturn;
  editingElement: EditableElement | undefined;
  editingElementBounds: { x: number; y: number; width: number; height: number } | null;
  stageTransform: ViewportTransform;
  showFormatToolbar: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  historyManagerRef: React.RefObject<any>;
}

export const CanvasOverlays: React.FC<CanvasOverlaysProps> = ({
  textEditor,
  editingElement,
  editingElementBounds,
  stageTransform,
  showFormatToolbar,
  containerRef,
  historyManagerRef,
}) => {
  if (!textEditor.isEditing || !editingElement) return null;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'transparent',
          zIndex: 9999,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            textEditor.saveText();
          }
        }}
      />

      <TextEditorOverlay
        element={editingElement}
        textStyle={textEditor.editingState.textStyle}
        onSave={textEditor.saveText}
        onCancel={textEditor.cancelEditing}
        stageTransform={stageTransform}
        screenBounds={editingElementBounds}
      />

      {showFormatToolbar && (
        <>
          {editingElement?.type === 'shape' && editingElementBounds ? (
            <ShapeTextToolbar
              textStyle={textEditor.editingState.textStyle}
              onStyleChange={textEditor.updateTextStyle}
              position={{
                x: editingElementBounds.x + editingElementBounds.width / 2,
                y: editingElementBounds.y - 50,
              }}
              elementBounds={editingElementBounds}
              containerRef={containerRef}
            />
          ) : editingElement?.type !== 'shape' && editingElementBounds ? (
            <FloatingTextFormatToolbar
              textStyle={textEditor.editingState.textStyle}
              onStyleChange={textEditor.updateTextStyle}
              onToggleBold={textEditor.toggleBold}
              onToggleItalic={textEditor.toggleItalic}
              position={textEditor.editingState.cursorPosition}
              stageTransform={stageTransform}
              elementBounds={editingElementBounds}
              containerRef={containerRef}
              backgroundColor={editingElement?.data?.backgroundColor || editingElement?.data?.fill}
              onBackgroundColorChange={(color) => {
                if (!editingElement) return;
                historyManagerRef.current?.modifyElement(editingElement.id, {
                  data: { ...editingElement.data, backgroundColor: color, fill: color },
                });
              }}
              strokeColor={editingElement?.data?.stroke}
              onStrokeColorChange={(color) => {
                if (!editingElement) return;
                historyManagerRef.current?.modifyElement(editingElement.id, {
                  data: { ...editingElement.data, stroke: color },
                });
              }}
            />
          ) : null}
        </>
      )}
    </>
  );
};
